import { useDispatch } from 'react-redux';
import {
  XCloseIcon,
  HelicopterIcon,
  TowTruckIcon,
  AviveHeartIcon,
  ArrowLeftIcon,
} from '@rsos/assets/icons';
import { Button, Text } from '@rsos/base-components';
import { displayToast } from '@rsos/components/capstone/base/Toast/displayToast';
import {
  closeResourcePanel,
  setResourcePanelView,
  useIsResourcePanelOpen,
  useResourcePanelView,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import AirAmbulanceForm from '../../../../AirAmbulanceForm';
import { ResourceOption } from '../types';
import ResourceOptionCard from './ResourceOptionCard';
import {
  PanelContainer,
  PanelListHeader,
  HeaderRow,
  CloseButton,
  PanelContent,
  PanelFooter,
  FormContent,
  FormHeader,
  BackButton,
} from './ResourceRequestPanel.styles';

const RESOURCE_OPTIONS: ResourceOption[] = [
  {
    id: 'air_ambulance',
    title: 'Air Ambulance',
    source: 'GMR',
    description:
      'Request emergency helicopter services for severe medical emergencies',
    Icon: HelicopterIcon,
  },
  {
    id: 'tow_truck',
    title: 'Tow Truck',
    source: 'Agero',
    description: 'Send a roadside assistance link to a caller',
    Icon: TowTruckIcon,
  },
  {
    id: 'aed',
    title: 'AED',
    source: 'Avive',
    description:
      'Notify AEDs and Avive App Users of a suspected cardiac arrest for immediate response',
    Icon: AviveHeartIcon,
  },
];

/**
 * ResourceRequestPanel is a right-hand slide-in panel that displays
 * resource options (Air Ambulance, Tow Truck, AED) that users can select.
 *
 * It can be opened from either the ResourceRequestCard in OverviewTab
 * or from the navigation header button.
 */
const ResourceRequestPanel = () => {
  const dispatch = useDispatch();
  const isOpen = useIsResourcePanelOpen();
  const resourcePanelView = useResourcePanelView();

  const handleClose = () => {
    dispatch(closeResourcePanel());
  };

  const handleBackToList = () => {
    dispatch(setResourcePanelView('list'));
  };

  const handleResourceClick = (resourceId: string) => {
    if (resourceId === 'air_ambulance') {
      dispatch(setResourcePanelView('air_ambulance'));
    } else {
      displayToast({
        message: 'Coming soon',
        options: { autoClose: 3000 },
      });
    }
  };

  if (resourcePanelView === 'air_ambulance') {
    return (
      <PanelContainer isOpen={isOpen} data-name="resource-request-panel">
        <FormHeader>
          <BackButton
            onClick={handleBackToList}
            data-name="resource-panel-back-button"
            aria-label="Back to resource list"
          >
            <ArrowLeftIcon color="secondaryText" height={12} width={12} />
          </BackButton>
          <div>
            <Text
              color="secondaryText"
              size="small"
              data-name="resource-panel-form-title"
            >
              New Resource Request
            </Text>
          </div>
          <PanelCloseButton handleClose={handleClose} />
        </FormHeader>
        <FormContent>
          <AirAmbulanceForm hideBackButton noBorder />
        </FormContent>
      </PanelContainer>
    );
  }

  return (
    <PanelContainer isOpen={isOpen} data-name="resource-request-panel">
      <PanelListHeader>
        <HeaderRow>
          <div>
            <Text
              color="primaryText"
              fontWeight="bold"
              size="large"
              lineHeight="1.2"
              data-name="resource-panel-title"
            >
              New Resource Request
            </Text>
            <Text
              color="secondaryText"
              size="normal"
              marginTop="4px"
              data-name="resource-panel-subtitle"
            >
              Choose a resource type to request
            </Text>
          </div>
          <PanelCloseButton handleClose={handleClose} />
        </HeaderRow>
      </PanelListHeader>

      <PanelContent>
        {RESOURCE_OPTIONS.map(option => (
          <ResourceOptionCard
            key={option.id}
            option={option}
            onClick={() => handleResourceClick(option.id)}
          />
        ))}
      </PanelContent>

      <PanelFooter>
        <Button
          btnType="secondary"
          grow
          noMargin
          onClick={handleClose}
          dataName="resource-panel-cancel-button"
        >
          Cancel
        </Button>
      </PanelFooter>
    </PanelContainer>
  );
};

function PanelCloseButton({ handleClose }: { handleClose: () => void }) {
  return (
    <CloseButton
      onClick={handleClose}
      data-name="resource-panel-close-button"
      aria-label="Close panel"
      style={{ marginLeft: 'auto' }}
    >
      <XCloseIcon color="primaryText" height={11} width={11} />
    </CloseButton>
  );
}

export default ResourceRequestPanel;
